/**
 * Service Worker registration
 * Handles caching of audio and font assets for faster subsequent loads
 */

let registration: ServiceWorkerRegistration | null = null;

export async function registerServiceWorker() {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    console.log('[SW] Service Workers not supported');
    return;
  }

  try {
    registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
    });

    console.log('[SW] Service Worker registered:', registration.scope);

    // Check for updates periodically (browser already checks on page load)
    // Reduced frequency to avoid InvalidStateError when SW is updating
    const updateInterval = setInterval(async () => {
      try {
        // More defensive checks before attempting update
        if (!registration) {
          clearInterval(updateInterval);
          return;
        }
        
        // Only update if registration is still valid and has an active worker
        if (registration.active && registration.scope) {
          // Wrap in timeout to prevent AbortError from blocking
          const updatePromise = registration.update();
          const timeoutPromise = new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Update timeout')), 30000)
          );
          
          await Promise.race([updatePromise, timeoutPromise]);
        }
      } catch (error) {
        // Silently ignore update errors - browser handles updates automatically
        // Common errors: InvalidStateError, AbortError (timeout), SecurityError
        // These are expected and don't affect functionality
      }
    }, 3600000); // Check every hour instead of every minute

    // Listen for updates
    registration.addEventListener('updatefound', () => {
      const newWorker = registration!.installing;
      if (!newWorker) return;

      newWorker.addEventListener('statechange', () => {
        if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
          console.log('[SW] New version available - will activate on next page load');
          // Optionally notify user of update
          // You could dispatch a custom event here to show a "Update available" banner
        }
      });
    });

    // Handle controller change (new SW activated)
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      console.log('[SW] New Service Worker activated');
    });

  } catch (error) {
    console.warn('[SW] Service Worker registration failed:', error);
  }
}

export async function unregisterServiceWorker() {
  if (!registration) return;
  
  try {
    await registration.unregister();
    console.log('[SW] Service Worker unregistered');
  } catch (error) {
    console.warn('[SW] Service Worker unregister failed:', error);
  }
}

export async function clearCache() {
  if (!registration) return;
  
  try {
    // Send message to SW to clear cache
    registration.active?.postMessage({ type: 'CLEAR_CACHE' });
    console.log('[SW] Cache clear requested');
  } catch (error) {
    console.warn('[SW] Cache clear failed:', error);
  }
}
